import * as angular from "angular";
import { Feature } from "../../models/feature.model";
import { ToastUtils } from "../../helper/toast.helper";
import { DialogUtils } from "../../helper/dialog.helper";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";

interface CanvassStatusItem {
    id: number;
    name: string;
    color?: string;
}

const ConversionRateConfigComponent: any = {
    selector: "vcConversionRateConfig",
    templateUrl:
        "/angular/components/conversion-rate-config/conversion-rate-config.component.html",
    bindings: {
        feature: "<",
        canvassStatuses: "<",
    },
    controller: class ConversionRateConfigController {
        public feature: Feature;
        public canvassStatuses: CanvassStatusItem[] = [];
        public numeratorIds: number[] = [];
        public denominatorIds: number[] = [];
        public useAllAttempts: boolean = true;
        public basis: "interactions" | "houses" = "interactions";

        public readonly isOrganisationCampaign: boolean;
        public readonly isLeaderView: boolean;
        public readonly canEdit: boolean;

        private readonly $scope: ng.IScope;
        private readonly $location: ng.ILocationService;
        private readonly dialog: DialogUtils;
        private readonly toast: ToastUtils;
        private statusLookup: Record<number, CanvassStatusItem> = {};
        // Snapshot of the persisted selection. Used to detect unsaved changes
        // when the user tries to navigate away.
        private baselineNumeratorIds: number[] = [];
        private baselineDenominatorIds: number[] = [];
        private baselineUseAllAttempts: boolean = true;
        private baselineBasis: "interactions" | "houses" = "interactions";
        // Set when the user has confirmed "leave" so the listener doesn't
        // re-prompt while we replay the navigation programmatically.
        private bypassUnsavedPrompt: boolean = false;

        constructor(
            $scope: ng.IScope,
            $rootScope: ng.IRootScopeService,
            $location: ng.ILocationService,
            DialogUtils: DialogUtils,
            ToastUtils: ToastUtils,
            CampaignDataProvider: CampaignDataProvider,
            __app_type: string
        ) {
            this.$scope = $scope;
            this.$location = $location;
            this.dialog = DialogUtils;
            this.toast = ToastUtils;
            this.isOrganisationCampaign =
                CampaignDataProvider.activeCampaign.isOrganisationCampaign();
            this.isLeaderView = __app_type === "LEADER";
            // Campaigns inside an organisation read from the org-level config,
            // so the campaign-level page is view-only — only the leader view
            // can edit. Standalone campaigns can always edit.
            this.canEdit = !this.isOrganisationCampaign || this.isLeaderView;

            if (!this.canEdit) return;

            // Matches the unsaved-changes pattern in create-event and
            // create-widget. Registered on $rootScope (not the component's
            // isolate $scope) so the listener fires regardless of whether
            // ng-if has unmounted the component on a tab switch.
            const offLocationChange: () => void = $rootScope.$on(
                "$locationChangeStart",
                (event: ng.IAngularEvent, newUrl: string): void => {
                    if (this.bypassUnsavedPrompt) return;
                    if (!this.isDirty()) return;

                    // $mdDialog is async, but $locationChangeStart is sync —
                    // block the navigation now, then re-issue it if the user
                    // confirms via the dialog.
                    event.preventDefault();
                    this.promptUnsavedChanges(newUrl);
                }
            );

            window.onbeforeunload = (e: BeforeUnloadEvent): string | undefined => {
                if (!this.isDirty()) return undefined;
                const message: string =
                    "You have unsaved changes to the conversion rate. Are you sure you want to leave this page?";
                e = e || (window.event as BeforeUnloadEvent);
                if (e) {
                    e.returnValue = message;
                }
                return message;
            };

            this.$scope.$on("$destroy", (): void => {
                offLocationChange();
                window.onbeforeunload = undefined;
            });
        }

        private promptUnsavedChanges(newUrl: string): void {
            this.dialog
                .show({
                    component: "vc-unsaved-changes-dialog",
                    size: "small",
                    locals: {},
                })
                .then(
                    (): void => {
                        // User chose "Yes, leave" — replay the original
                        // navigation. Strip the origin off so $location.url
                        // gets just the path/search/hash it expects.
                        this.bypassUnsavedPrompt = true;
                        window.onbeforeunload = undefined;
                        const hashIndex: number = newUrl.indexOf("#");
                        const target: string =
                            hashIndex >= 0
                                ? newUrl.substring(hashIndex + 1)
                                : newUrl;
                        this.$location.url(target);
                    },
                    (): void => {
                        // User cancelled — stay on the page.
                    }
                );
        }

        $onChanges(changes: any): void {
            if (changes.feature && this.feature) {
                this.loadFromFeature();
            }
            if (changes.canvassStatuses) {
                this.rebuildStatusLookup();
                // If the feature loaded before the statuses, the persisted
                // ids couldn't be filtered against the active status list
                // yet. Re-run the strip now that we know which ids exist.
                if (this.feature) {
                    this.dropStaleStatusIds();
                    this.snapshotBaseline();
                }
            }
        }

        private rebuildStatusLookup(): void {
            this.statusLookup = {};
            (this.canvassStatuses ?? []).forEach((s: CanvassStatusItem): void => {
                this.statusLookup[s.id] = s;
            });
        }

        private toIntId(raw: unknown): number {
            return parseInt(String(raw), 10);
        }

        private normalizeIds(raw: unknown): number[] {
            if (!Array.isArray(raw)) return [];
            const ints: number[] = raw
                .map((n: unknown): number => this.toIntId(n))
                .filter((n: number): boolean => !isNaN(n));
            return Array.from(new Set(ints));
        }

        private loadFromFeature(): void {
            const extra: any = this.feature?.attributes?.extra ?? null;
            this.basis = extra && extra.basis === "houses" ? "houses" : "interactions";
            const defaultNumerator: number[] = [1];

            if (!extra || !Array.isArray(extra.numerator) || extra.numerator.length === 0) {
                this.numeratorIds = defaultNumerator.slice();
                this.useAllAttempts = true;
                this.denominatorIds = [];
                this.snapshotBaseline();
                return;
            }

            this.numeratorIds = this.normalizeIds(extra.numerator);
            if (this.numeratorIds.length === 0) {
                this.numeratorIds = defaultNumerator.slice();
            }

            if (extra.denominator === null || extra.denominator === undefined) {
                this.useAllAttempts = true;
                this.denominatorIds = [];
            } else if (Array.isArray(extra.denominator) && extra.denominator.length > 0) {
                this.denominatorIds = this.normalizeIds(extra.denominator);
                this.useAllAttempts = this.denominatorIds.length === 0;
            } else {
                this.useAllAttempts = true;
                this.denominatorIds = [];
            }

            this.dropStaleStatusIds();
            this.snapshotBaseline();
        }

        /**
         * Strips numerator/denominator ids that no longer exist in the
         * active status list. Guards against pre-existing data left over
         * from before the delete/migrate flow blocked removing referenced
         * statuses — the cleaned state silently becomes the new baseline,
         * so isDirty() stays false until the user makes a real change.
         * No-op when the status list hasn't loaded yet.
         */
        private dropStaleStatusIds(): void {
            if (!this.canvassStatuses || this.canvassStatuses.length === 0) {
                return;
            }
            const known: Set<number> = new Set(
                this.canvassStatuses.map((s: CanvassStatusItem): number =>
                    this.toIntId(s.id)
                )
            );
            this.numeratorIds = this.numeratorIds.filter((id: number): boolean =>
                known.has(id)
            );
            this.denominatorIds = this.denominatorIds.filter((id: number): boolean =>
                known.has(id)
            );
        }

        private snapshotBaseline(): void {
            this.baselineNumeratorIds = this.numeratorIds.slice();
            this.baselineDenominatorIds = this.denominatorIds.slice();
            this.baselineUseAllAttempts = this.useAllAttempts;
            this.baselineBasis = this.basis;
        }

        private sameIds(a: number[], b: number[]): boolean {
            if (a.length !== b.length) return false;
            const sortedA: number[] = a.slice().sort();
            const sortedB: number[] = b.slice().sort();
            return sortedA.every((v: number, i: number): boolean => v === sortedB[i]);
        }

        isDirty(): boolean {
            if (this.basis !== this.baselineBasis) return true;
            if (this.useAllAttempts !== this.baselineUseAllAttempts) return true;
            if (!this.sameIds(this.numeratorIds, this.baselineNumeratorIds)) return true;
            // The denominator list is irrelevant when "all interactions" is on,
            // since it's stored as null. Don't flag it as dirty in that case.
            if (this.useAllAttempts) return false;
            return !this.sameIds(this.denominatorIds, this.baselineDenominatorIds);
        }

        toggleNumerator(rawId: unknown): void {
            const id: number = this.toIntId(rawId);
            if (isNaN(id)) return;
            const idx: number = this.numeratorIds.indexOf(id);
            if (idx === -1) {
                this.numeratorIds = this.numeratorIds.concat([id]);
                // A numerator status must always also be in the denominator,
                // otherwise the conversion rate can exceed 100%. Lock it in
                // here so the UI doesn't let users get into that state.
                if (this.denominatorIds.indexOf(id) === -1) {
                    this.denominatorIds = this.denominatorIds.concat([id]);
                }
            } else {
                this.numeratorIds = this.numeratorIds.filter((existing: number): boolean => existing !== id);
            }
        }

        toggleDenominator(rawId: unknown): void {
            const id: number = this.toIntId(rawId);
            if (isNaN(id)) return;
            const idx: number = this.denominatorIds.indexOf(id);
            if (idx === -1) {
                this.denominatorIds = this.denominatorIds.concat([id]);
            } else {
                this.denominatorIds = this.denominatorIds.filter((existing: number): boolean => existing !== id);
            }
        }

        isNumeratorChecked(rawId: unknown): boolean {
            return this.numeratorIds.indexOf(this.toIntId(rawId)) !== -1;
        }

        isDenominatorChecked(rawId: unknown): boolean {
            return this.denominatorIds.indexOf(this.toIntId(rawId)) !== -1;
        }

        isDenominatorDisabled(rawId: unknown): boolean {
            if (!this.canEdit) return true;
            if (this.useAllAttempts) return true;
            return this.isNumeratorChecked(rawId);
        }

        /**
         * When the user switches off "Compare against all logged interactions",
         * make sure every numerator status is reflected in the denominator. The
         * numerator rows on the right are locked (disabled) and should appear
         * checked — without this they'd look unchecked on the initial state.
         */
        onUseAllAttemptsChange(): void {
            if (this.useAllAttempts) return;
            this.numeratorIds.forEach((id: number): void => {
                if (this.denominatorIds.indexOf(id) === -1) {
                    this.denominatorIds = this.denominatorIds.concat([id]);
                }
            });
        }

        statusName(id: number): string {
            return this.statusLookup[id]?.name ?? `#${id}`;
        }

        statusColor(id: number): string {
            return this.statusLookup[id]?.color ?? "#9e9e9e";
        }

        canSave(): boolean {
            if (!this.canEdit) return false;
            if (this.numeratorIds.length === 0) return false;
            if (!this.useAllAttempts && this.denominatorIds.length === 0) return false;
            return this.numeratorNotInDenominator().length <= 0;
        }

        /**
         * Numerator statuses that don't appear in the custom denominator.
         * Returns [] when "Compare against all interactions" is on, since the
         * subset invariant is then trivially satisfied.
         */
        numeratorNotInDenominator(): number[] {
            if (this.useAllAttempts) return [];
            return this.numeratorIds.filter(
                (id: number): boolean => this.denominatorIds.indexOf(id) === -1
            );
        }

        save(): void {
            if (this.numeratorIds.length === 0) {
                this.toast.error("Pick at least one status for the numerator.");
                return;
            }
            if (!this.useAllAttempts && this.denominatorIds.length === 0) {
                this.toast.error("Pick at least one status for the denominator, or use all interactions.");
                return;
            }
            const missing: number[] = this.numeratorNotInDenominator();
            if (missing.length > 0) {
                const names: string = missing
                    .map((id: number): string => this.statusName(id))
                    .join(", ");
                this.toast.error(`Add ${names} to the denominator, or remove from the numerator. Otherwise the formula can exceed 100%.`);
                return;
            }

            this.feature.attributes.extra = {
                numerator: this.numeratorIds.slice(),
                denominator: this.useAllAttempts ? null : this.denominatorIds.slice(),
                basis: this.basis,
            };

            this.feature.update().then(
                (): void => {
                    this.snapshotBaseline();
                    this.toast.success("Conversion rate settings saved");
                },
                (): void => {
                    this.toast.error();
                }
            );
        }
    },
};

angular
    .module("conversionRateConfig", [])
    .component(
        ConversionRateConfigComponent.selector,
        ConversionRateConfigComponent
    );
